import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Thread } from "./Thread";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// Post-payment ROUTER. The Stripe Payment Link confirmation URL lands here
// with ?session_id= (or, on the same device, the /pay stamp). The purchase is
// verified server-side against the webhook record, then the parent is
// forwarded to their exact private welcome page — /virtual-newborn …
// /in-home-toddler — which carries the welcome, the intake, and the next
// steps. Fires NO purchase/conversion analytics events — the Stripe webhook
// remains the only source of truth for purchases.
//
// Failsafe: never guess. If service/stage can't be resolved (manual visit,
// different browser, missing stamp), no route and no intake is shown — the
// purchase is flagged needs_review for Andrea's hub, and the parent gets a
// warm "Andrea will send it personally" page instead.
const API = process.env.REACT_APP_BACKEND_URL;
const STAMP_KEY = "tnpc_pending_payment";
const STAMP_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const PaymentComplete = () => {
  const reduce = useReducedMotion();
  const [failed, setFailed] = useState(false); // true = failsafe (never-guess)
  const [email, setEmail] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [lookupSent, setLookupSent] = useState(false);
  const [stagePick, setStagePick] = useState(null); // verified purchase, stage unknown → ask the parent
  const [stageBusy, setStageBusy] = useState(false);

  // A verified purchase that arrived with no stage (e.g. paid via the raw
  // Stripe link) is never guessed — the parent picks the stage, which
  // unlocks the right intake and triggers the full welcome email.
  const pickStage = async (stage) => {
    setStageBusy(true);
    setLookupError(null);
    try {
      const resp = await fetch(`${API}/api/pay/set-stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: stagePick.session_id, client_stage: stage }),
      });
      if (!resp.ok) throw new Error(`set-stage failed: ${resp.status}`);
      const data = await resp.json();
      if (data.service_type && data.client_stage) {
        window.location.replace(`/${data.service_type}-${data.client_stage}?session_id=${stagePick.session_id}`);
        return;
      }
      throw new Error("incomplete");
    } catch {
      setLookupError("Something didn't save — try again, or text Andrea below.");
    }
    setStageBusy(false);
  };

  // Email-independent recovery, privacy-safe: a parent whose welcome email
  // never arrived (or who lost the session in the URL) enters their checkout
  // email and their private link is re-sent to that inbox. The server answer
  // is identical whether or not a payment exists — this page never learns
  // anything about the purchase, so someone else's email alone can never
  // expose their information.
  const lookupByEmail = async (e) => {
    e.preventDefault();
    setLookupBusy(true);
    setLookupError(null);
    try {
      const resp = await fetch(`${API}/api/pay/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) throw new Error(`lookup failed: ${resp.status}`);
      setLookupSent(true);
    } catch {
      setLookupError("That didn't go through — wait a few minutes and try again, or text Andrea below.");
    }
    setLookupBusy(false);
  };

  useEffect(() => {
    document.title = "You're officially in — The Nurtured Parent Co.";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,follow";
    document.head.appendChild(meta);
    window.scrollTo(0, 0);

    let cancelled = false;
    (async () => {
      try {
        const sessionId = new URLSearchParams(window.location.search).get("session_id");
        let body = null;
        if (sessionId) {
          body = { session_id: sessionId };
        } else {
          const raw = localStorage.getItem(STAMP_KEY);
          if (raw) {
            const stamp = JSON.parse(raw);
            const fresh = stamp?.ts && Date.now() - stamp.ts < STAMP_TTL_MS;
            const staged = ["newborn", "infant", "toddler"].includes(stamp?.stage);
            if (fresh && staged) {
              body = { slug: stamp.slug, service_type: stamp.service, client_stage: stamp.stage };
            }
          }
        }
        if (!body) {
          if (!cancelled) setFailed(true);
          return;
        }
        const resp = await fetch(`${API}/api/pay/intake`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!resp.ok) throw new Error(`lookup failed: ${resp.status}`);
        const data = await resp.json();
        if (data.needs_stage && data.session_id) {
          if (!cancelled) setStagePick({ session_id: data.session_id, service: data.service_type });
          return;
        }
        if (data.service_type && data.client_stage) {
          const route = `/${data.service_type}-${data.client_stage}`;
          window.location.replace(sessionId ? `${route}?session_id=${sessionId}` : route);
          return;
        }
        if (!cancelled) setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
  }, []);

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="payment-complete-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <a href="/" data-testid="payment-logo" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </a>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20">
        <section className="py-16 lg:py-24">
          {!failed && !stagePick && (
            <motion.p {...rise(0.15)} data-testid="payment-routing" className="text-base font-normal text-smoke">
              One moment — bringing you to your welcome page…
            </motion.p>
          )}

          {stagePick && (
            <>
              <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                Payment received
              </motion.p>
              <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl leading-[1.02] tracking-tight">
                One quick <span className="italic text-clay">question.</span>
              </motion.h1>
              <motion.p {...rise(0.4)} className="mt-10 max-w-xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
                Which stage is your little one in? Your answer unlocks the right
                intake — Andrea reviews every word personally.
              </motion.p>
              <motion.div {...rise(0.5)} className="mt-8 flex flex-col gap-3 max-w-xl">
                {[
                  ["newborn", "Newborn — 0–3 months"],
                  ["infant", "Infant — 4–17 months"],
                  ["toddler", "Toddler — 18+ months"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`stage-pick-${key}`}
                    onClick={() => pickStage(key)}
                    disabled={stageBusy}
                    className="rounded-full border border-ink/15 bg-white px-7 py-4 text-left text-base text-ink hover:border-ink/40 disabled:opacity-50 transition-colors duration-200"
                  >
                    {label}
                  </button>
                ))}
              </motion.div>
              {lookupError && (
                <p data-testid="payment-email-error" className="mt-3 max-w-xl text-sm text-clay">{lookupError}</p>
              )}
            </>
          )}

          {failed && (
            <>
              <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                Payment received
              </motion.p>
              <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl leading-[1.02] tracking-tight">
                You&rsquo;re <span className="italic text-clay">officially in.</span>
              </motion.h1>
              <motion.div {...rise(0.4)} data-testid="intake-pending" className="mt-10 max-w-xl space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
                <p>
                  Welcome to The Nurtured Parent Co. — Andrea now has you on
                  her client list.
                </p>
                <p>
                  She sends every family&rsquo;s private welcome link personally
                  — it will reach you shortly by email, and it walks you through
                  your intake and the next steps.
                </p>
                {!lookupSent && (
                  <p>Can&rsquo;t find it? Enter the email you used at checkout and we&rsquo;ll send your private link straight to that inbox:</p>
                )}
              </motion.div>
              {lookupSent ? (
                <motion.p {...rise(0.5)} data-testid="payment-email-sent" className="mt-6 max-w-xl text-base sm:text-lg font-normal leading-relaxed text-smoke">
                  If a confirmed payment exists for <span className="text-ink">{email}</span>, your private link is on its way to that inbox — it comes from The Nurtured Parent Co., so check spam or promotions too. Nothing there within a few minutes? Text Andrea below.
                </motion.p>
              ) : (
                <motion.form {...rise(0.5)} onSubmit={lookupByEmail} className="mt-6 max-w-xl flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="payment-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 rounded-full border border-ink/15 bg-white px-5 py-3.5 text-base text-ink placeholder:text-smoke/60 focus:outline-none focus:border-ink/40 transition-colors duration-200"
                  />
                  <button
                    data-testid="payment-email-lookup"
                    type="submit"
                    disabled={lookupBusy}
                    className="rounded-full bg-ink text-ivory font-medium px-7 py-3.5 text-base hover:bg-espresso disabled:opacity-50 transition-colors duration-300"
                  >
                    {lookupBusy ? "Sending…" : "Send my private link"}
                  </button>
                </motion.form>
              )}
              {lookupError && !lookupSent && (
                <p data-testid="payment-email-error" className="mt-3 max-w-xl text-sm text-clay">{lookupError}</p>
              )}
              <motion.div {...rise(0.55)} className="mt-9">
                <a
                  data-testid="intake-text-andrea"
                  href={`sms:${ANDREA_PHONE}`}
                  onClick={(e) => {
                    trackEvent("text_andrea_click", { location: "payment_complete" });
                    contactClick("text")(e);
                  }}
                  className="inline-flex items-center gap-2.5 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300"
                >
                  Text Andrea — {ANDREA_PHONE_DISPLAY}
                </a>
              </motion.div>
            </>
          )}
        </section>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
